import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import { DefaultTabItemStyle } from '@/components/Tab/TabHeader';
import Tabs from '@/components/Tab/Tabs';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import Tooltip from '@/components/Tooltip/TooltipV2';
import {
  TabAsyncContentWrapper,
  TabContentStyle,
} from '@/features/Commons/DetailStyles';
import FeedbackTab from '@/features/Commons/FeedbackTab';
import SentimentTab from '@/features/Commons/SentimentTab';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import service from '@/services/index';
import useCustomerStore from '@/stores/CustomerStore';
import type { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useToastStore from '@/stores/pure/ToastStore';
import { detailModalStyle } from '@/styles/style';

import CustomerInfoTab from './CustomerInfoTab';

type ModalProps = {
  customerId: string;
  pos: number;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const CustomerDetailModal = (props: ModalProps) => {
  const { isEditor } = useUserRole();

  const { removeCustomer, changeCustomerAttr, fetchCustomer } =
    useCustomerStore(
      ({ removeCustomer, changeCustomerAttr, fetchCustomer }) => ({
        removeCustomer,
        changeCustomerAttr,
        fetchCustomer,
      }),
    );

  const setPopup = usePopupStore((state) => state.setPopup);
  const addDefaultToast = useToastStore((state) => state.addDefaultToast);

  const customerMap = useCustomerStore((state) => state.customerMap);

  const { data, isLoading } = useQuery({
    queryKey: ['customer-linked-feedbacks', props.customerId],
    queryFn: () => service.contact.fetchLinkedFeedback(props.customerId),
  });

  const customer = useMemo(() => {
    return customerMap[props.customerId];
  }, [customerMap, props.customerId]);

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        id: 'sentiment',
        text: 'Sentiment',
        content: (
          <TabAsyncContentWrapper status={!isLoading}>
            {data && (
              <SentimentTab
                alias={props.customerId}
                data={data}
                onSelectNewModal={props.onSelectNewModal}
              />
            )}
          </TabAsyncContentWrapper>
        ),
      },
      {
        id: 'feedback',
        text: `Feedback (${isLoading ? 0 : data?.feedbacks.length})`,
        content: (
          <TabAsyncContentWrapper status={!isLoading}>
            {data && (
              <FeedbackTab
                alias={props.customerId}
                data={data}
                onSelectNewModal={props.onSelectNewModal}
              />
            )}
          </TabAsyncContentWrapper>
        ),
      },
      {
        id: 'contact_info',
        text: 'Contact Info',
        content: (
          <TabContentStyle>
            <CustomerInfoTab customerId={props.customerId} />
          </TabContentStyle>
        ),
      },
    ];
  }, [data, isLoading, props.customerId, props.onSelectNewModal]);

  const [selectedTab, selectTab] = useTabs(tabs, props.customerId);

  const deleteCustomer = () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete contact',
      message: 'Are you sure you want to delete this contact?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        if (!customer) return;
        removeCustomer(customer.alias);
        props.closeModal();
        addDefaultToast('Contact Deleted', 'success');
      },
    });
  };

  const [customerName, setCustomerName] = useState<string>('');

  useEffect(() => {
    if (!customer) {
      fetchCustomer(props.customerId);
      return;
    }
  }, [customer, fetchCustomer, props.customerId]);

  useEffect(() => {
    customer?.name && setCustomerName(customer.name);
  }, [customer?.name]);

  useTrackModal({
    domain: 'customers',
    alias: props.customerId,
  });
  useKeymap('Escape', props.closeModal);

  return (
    <ModalStyle status={true} pos={props.pos}>
      <Flex j="space-between" a="center" style={{ padding: '16px 32px' }}>
        <Flex></Flex>
        <Flex g="8px">
          {isEditor && (
            <Tooltip tooltipNode="Delete the contact">
              <IconButton icon={<DeleteOutlined />} onClick={deleteCustomer} />
            </Tooltip>
          )}
          <IconButton
            icon={<CloseOutlined />}
            onClick={props.closeModal}
            tooltip="Close"
            shortcut="ESC"
          />
        </Flex>
      </Flex>
      <Divider direction="h" />
      <Padded g="16px">
        <FlexibleTitleInput
          placeholder="Name"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          readOnly={!isEditor}
          onBlur={(e) => {
            if (!customer) return;
            changeCustomerAttr(customer.alias, 'name', e.target.value);
          }}
        />
      </Padded>
      <Space h="16px" />
      <Tabs tabs={tabs} selectedTabId={selectedTab}>
        <Tabs.TabHeader
          scrollEnabled={false}
          tabSize="m"
          tabColor="primary"
          tabStyle={StyledCustomTab}
          onSelect={(id) => {
            selectTab(id || null);
          }}
        />
        <Tabs.TabBody />
      </Tabs>
    </ModalStyle>
  );
};

export default CustomerDetailModal;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;

  ${detailModalStyle}
`;

const Padded = styled(VFlex)`
  padding: 16px 32px 0px 32px;
`;

const StyledCustomTab = styled(DefaultTabItemStyle)`
  max-width: calc(100% / 3);
  padding: 16px 12px;
`;
